import { useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { OfferSectionBlock, type SectionGroup } from "./OfferSectionBlock";
import { classifyOfferDragDrop } from "./offer-line-nav";
import type { OfferForCalc } from "@/lib/offer-calc";
import type { OfferLine } from "@/hooks/use-offers";
import type { OfferSection } from "@/hooks/use-offer-sections";

interface Props {
  lines: OfferLine[];
  /** Tilvalgsseksjoner (VIB-663e). Tom liste = bare hovedbudsjett. */
  sections: OfferSection[];
  /**
   * Read-only-modus (ikke-forfatter/ikke-admin). Skjuler "legg til"-knapper
   * og deaktiverer celle-inputs/reorder/slett nedover i radene.
   * VIB-offer-author-gating.
   */
  readOnly?: boolean;
  hourlyRate: number;
  /**
   * Katalogens timesats per linje (mal → kr/t, ellers `hourlyRate`). Brukes
   * som fallback-sats og som tidsbasis ved fast → t/d (VIB-2097).
   */
  rateForLine?: (line: OfferLine) => number;
  /** Katalogens default overtidssats (kr/t) per linje (VIB-2087). */
  overtimeRateForLine?: (line: OfferLine) => number;
  /** ID-en til den sist tillagte linjen — fokuseres automatisk i mengde-feltet. */
  autoFocusLineId?: string | null;
  /**
   * Seksjon som "Kopier som bilde" er aktiv på. "main" for hovedbudsjett,
   * en seksjons-id for tilvalg, eller null hvis ingen kopi-flyt pågår.
   * Brukes for å vise spinner i riktig knapp.
   */
  copyingSectionKey?: string | null;
  /** Offer-level overhead/discount — anvendes per seksjon. */
  offerTotals: OfferForCalc;
  /** custom_terms_text for hovedbudsjettet. Lagres til offers.custom_terms_text. */
  mainTermsText: string;
  onMainTermsTextChange: (text: string) => void;
  /** Lagre custom_terms_text på en tilvalgsseksjon. */
  onSectionTermsTextChange: (sectionId: string, text: string) => void;
  onChange: (id: string, patch: Partial<OfferLine>) => void;
  onDelete: (id: string) => void;
  /**
   * Åpner katalog-dialogen for å velge oppføring og legge til linje.
   * sectionId=null betyr hovedbudsjett; en streng betyr at linja
   * skal opprettes i den tilvalgsseksjonen (VIB-663e-fix).
   */
  onAddLine: (sectionId: string | null) => void;
  /** Åpner dialog for å opprette ny tilvalgsseksjon. */
  onAddSection: () => void;
  /** Slett en hel tilvalgsseksjon (linjer beholdes, flyttes til hovedbudsjett). */
  onDeleteSection: (sectionId: string) => void;
  /** VIB-663e-fix: kopier kun denne seksjonen som bilde. null = hovedbudsjett. */
  onCopySectionImage: (sectionId: string | null) => void;
  /** Reorder via drag-and-drop. */
  onReorder: (sourceId: string, targetId: string) => void;
  /** Option-dra eller ⌘D: dupliser. targetId=null = rett etter kilden. */
  onDuplicate?: (sourceId: string, targetId: string | null) => void;
  /** Mobile tap → åpne edit-sheet. */
  onLineTap: (lineId: string) => void;
  /** Mobile reorder via opp/ned-knapp. Bytter sort_order med nabolinje. */
  onMoveLine: (lineId: string, dir: "up" | "down") => void;
}

/**
 * Linjer gruppert etter section_id (hoved + n tilvalg, VIB-663e), så etter fase.
 * Hver section vises som en blokk med tittel, fase-grupper og sub-totalsum.
 *
 * Drag-drop mellom faser endrer linjens phase. Drag mellom seksjoner er
 * ikke støttet i denne versjonen — bruk "Flytt til"-meny på raden (TODO).
 */
export function OfferLineTable({
  lines,
  sections,
  readOnly = false,
  hourlyRate,
  rateForLine,
  overtimeRateForLine,
  autoFocusLineId,
  copyingSectionKey,
  offerTotals,
  mainTermsText,
  onMainTermsTextChange,
  onSectionTermsTextChange,
  onChange,
  onDelete,
  onAddLine,
  onAddSection,
  onDeleteSection,
  onCopySectionImage,
  onReorder,
  onDuplicate,
  onLineTap,
  onMoveLine,
}: Props) {
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropTargetId, setDropTargetId] = useState<string | null>(null);
  const [copyDrag, setCopyDrag] = useState(false);
  const dragIdRef = useRef<string | null>(null);
  const copyDragRef = useRef(false);

  function setCopyMode(next: boolean) {
    copyDragRef.current = next;
    setCopyDrag(next);
  }

  // Bygg grupper: hovedbudsjett først, så hver seksjon etter sort_order.
  const groups = useMemo<SectionGroup[]>(() => {
    const mainLines = lines.filter((l) => !l.section_id);
    const result: SectionGroup[] = [];
    if (mainLines.length > 0 || sections.length === 0) {
      result.push({
        section: null,
        lines: mainLines,
        subtotal: mainLines.reduce((s, l) => s + (l.amount ?? 0), 0),
      });
    }
    const sortedSections = [...sections].sort(
      (a, b) => a.sort_order - b.sort_order,
    );
    for (const section of sortedSections) {
      const sectionLines = lines.filter((l) => l.section_id === section.id);
      result.push({
        section,
        lines: sectionLines,
        subtotal: sectionLines.reduce((s, l) => s + (l.amount ?? 0), 0),
      });
    }
    return result;
  }, [lines, sections]);

  function dragHandlersFor(id: string): React.HTMLAttributes<HTMLDivElement> {
    if (readOnly) return {};
    return {
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = "copyMove";
        e.dataTransfer.setData("text/plain", id);
        dragIdRef.current = id;
        setDragId(id);
        setCopyMode(e.altKey);
        setDropTargetId(null);
      },
      onDragOver: (e) => {
        e.preventDefault();
        // Sticky copy: Safari sender ofte altKey=false på dragover/drop.
        if (e.altKey) setCopyMode(true);
        const isCopy = !!onDuplicate && (e.altKey || copyDragRef.current);
        e.dataTransfer.dropEffect = isCopy ? "copy" : "move";
        const source = dragIdRef.current;
        const kind = classifyOfferDragDrop(source, id, isCopy);
        setDropTargetId(kind === "none" ? null : id);
      },
      onDrop: (e) => {
        e.preventDefault();
        const source = dragIdRef.current;
        // Safari sender ofte altKey=false på drop; behold sist sett copy-modus.
        const isCopy = !!onDuplicate && (e.altKey || copyDragRef.current);
        const kind = classifyOfferDragDrop(source, id, isCopy);
        if (kind === "copy" && source && onDuplicate) {
          onDuplicate(source, source === id ? null : id);
        } else if (kind === "move" && source) {
          onReorder(source, id);
        }
        dragIdRef.current = null;
        setDragId(null);
        setDropTargetId(null);
        setCopyMode(false);
      },
      onDragEnd: () => {
        dragIdRef.current = null;
        setDragId(null);
        setDropTargetId(null);
        setCopyMode(false);
      },
    };
  }

  if (lines.length === 0 && sections.length === 0) {
    return (
      <div className="border rounded-md p-10 text-center bg-background">
        <p className="text-sm font-medium mb-1.5">Ingen linjer ennå</p>
        {!readOnly && (
          <>
            <p className="text-xs text-muted-foreground mb-5">
              Søk i katalogen øverst (⌘L), eller legg til en blank linje.
            </p>
            <button
              type="button"
              onClick={() => onAddLine(null)}
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium",
                "border bg-background hover:bg-muted/40 transition-colors",
              )}
            >
              + Ny linje
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2.5" data-offer-table>
      {groups.map((group) => {
        const isMain = group.section === null;
        const termsText = isMain
          ? mainTermsText
          : group.section!.custom_terms_text ?? "";
        const handleTermsChange = isMain
          ? onMainTermsTextChange
          : (text: string) => onSectionTermsTextChange(group.section!.id, text);
        return (
          <OfferSectionBlock
            key={group.section?.id ?? "main"}
            group={group}
            readOnly={readOnly}
            hourlyRate={hourlyRate}
            rateForLine={rateForLine}
            overtimeRateForLine={overtimeRateForLine}
            autoFocusLineId={autoFocusLineId}
            copyingSectionKey={copyingSectionKey}
            dragId={dragId}
            dropTargetId={dropTargetId}
            copyDrag={copyDrag}
            dragHandlersFor={dragHandlersFor}
            offerTotals={offerTotals}
            termsText={termsText}
            onTermsTextChange={handleTermsChange}
            onChange={onChange}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
            onAddLine={onAddLine}
            onDeleteSection={onDeleteSection}
            onCopySectionImage={onCopySectionImage}
            onLineTap={onLineTap}
            onMoveLine={onMoveLine}
          />
        );
      })}

      {!readOnly && (
        <button
          type="button"
          onClick={onAddSection}
          className={cn(
            "w-full text-left px-3 py-1.5 text-xs font-medium rounded-md",
            "text-primary border border-dashed hover:bg-muted/30 transition-colors",
            "inline-flex items-center gap-1.5",
          )}
        >
          <SrIcon name="plus" className="w-3.5 h-3.5" />
          Legg til tilvalg
          <span className="ml-2 text-muted-foreground font-normal">
            Vis et alternativ (f.eks. Stillbildefotograf) som egen seksjon
          </span>
        </button>
      )}
    </div>
  );
}
